import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut,
  User as FirebaseUser
} from 'firebase/auth';
import { auth } from '../config/firebase';

/**
 * Email Verification Service
 * Handles Firebase email verification for user registration
 */
export class EmailVerificationService {
  /**
   * Register user with Firebase and send verification email
   */
  static async registerAndSendVerification(
    email: string,
    password: string
  ): Promise<{ firebaseUser: FirebaseUser; emailSent: boolean }> {
    try {
      // Create user in Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Send verification email
      await sendEmailVerification(firebaseUser, {
        url: window.location.origin + '/email-verified', // Redirect URL after verification
        handleCodeInApp: false
      });

      // Sign out the user until email is verified
      await signOut(auth);

      return { firebaseUser, emailSent: true };
    } catch (error: any) {
      console.error('Firebase registration error:', error);
      throw new Error(this.getFirebaseErrorMessage(error.code));
    }
  }

  /**
   * Check if user's email is verified
   */
  static async checkEmailVerification(
    email: string,
    password: string
  ): Promise<{ emailVerified: boolean; firebaseUser?: FirebaseUser }> {
    try {
      // Sign in to get latest user data
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Reload user data to get latest emailVerified status
      await firebaseUser.reload();

      const emailVerified = firebaseUser.emailVerified;

      // Sign out after checking
      await signOut(auth);

      return { emailVerified, firebaseUser };
    } catch (error: any) {
      console.error('Email verification check error:', error);
      throw new Error(this.getFirebaseErrorMessage(error.code));
    }
  }

  /**
   * Resend verification email
   */
  static async resendVerificationEmail(email: string, password: string): Promise<void> {
    try {
      // Sign in
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Check if already verified
      await firebaseUser.reload();
      if (firebaseUser.emailVerified) {
        await signOut(auth);
        throw new Error('Email is already verified');
      }

      // Resend verification email
      await sendEmailVerification(firebaseUser, {
        url: window.location.origin + '/email-verified',
        handleCodeInApp: false
      });

      // Sign out
      await signOut(auth);
    } catch (error: any) {
      console.error('Resend verification error:', error);
      throw new Error(this.getFirebaseErrorMessage(error.code));
    }
  }

  /**
   * Convert Firebase error codes to user-friendly messages
   */
  private static getFirebaseErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'This email is already registered. Please login instead.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.';
      default:
        return 'An error occurred. Please try again.';
    }
  }
}

export default EmailVerificationService;
